/**
 * sharing.ts - Sharing Module
 *
 * This module handles tournament sharing functionality including:
 * - Generating and toggling share links
 * - Sharing with specific users
 * - Managing shared users list
 * - Revoking access
 */

import { API_BASE_URL } from '../utils/constants';

/**
 * Shared user object
 */
interface SharedUser {
  userId: string;
  username: string;
  email: string;
}

/**
 * Share with user response
 */
interface ShareWithUserResponse {
  sharedWith: {
    userId: string;
    username: string;
  };
}

/**
 * Shared users list response
 */
interface SharedUsersResponse {
  sharedUsers: SharedUser[];
}

/**
 * Toggle share link visibility or generate new share link
 * @param tournamentId - The tournament ID
 */
export async function toggleShareLink(tournamentId: string): Promise<void> {
  const shareInfo = document.getElementById(`share-info-${tournamentId}`);

  // If share info exists, just toggle visibility
  if (shareInfo) {
    if (shareInfo.style.display === 'none') {
      shareInfo.style.display = 'block';
    } else {
      shareInfo.style.display = 'none';
    }
    return;
  }

  // Otherwise generate a new share link
  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/share`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });

    const data = await response.json();

    if (response.ok) {
      // Reload single tournament to show the share link
      if (window.loadSingleTournament) {
        await window.loadSingleTournament(tournamentId);
      }
      // Show the newly created share info
      setTimeout(() => {
        const newShareInfo = document.getElementById(`share-info-${tournamentId}`);
        if (newShareInfo) {
          newShareInfo.style.display = 'block';
        }
      }, 100);
    } else {
      const errorMsg = window.i18n ? window.i18n.t('share.errorGeneratingLink') : 'Failed to generate share link';
      alert(`Error: ${data.error || errorMsg}`);
    }
  } catch (error) {
    console.error('Error generating share link:', error);
    const errorMsg = window.i18n ? window.i18n.t('share.errorOccurred') : 'An error occurred while generating the share link';
    alert(errorMsg);
  }
}

/**
 * Generate share link (backward compatibility wrapper)
 * @param tournamentId - The tournament ID
 */
export async function generateShareLink(tournamentId: string): Promise<void> {
  // Keep for backward compatibility
  return toggleShareLink(tournamentId);
}

/**
 * Copy share link to clipboard
 * @param tournamentId - The tournament ID
 * @param event - The click event
 */
export async function copyShareLink(tournamentId: string, event: Event): Promise<void> {
  const input = document.getElementById(`share-url-${tournamentId}`) as HTMLInputElement | null;
  const button = event.target as HTMLButtonElement;

  if (!input) return;

  try {
    await navigator.clipboard.writeText(input.value);
    const originalText = button.textContent;
    button.textContent = '✓ Copied!';
    button.style.backgroundColor = '#28a745';

    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
    }, 2000);
  } catch (err) {
    // Fallback for older browsers
    input.select();
    input.setSelectionRange(0, 99999);
    try {
      document.execCommand('copy');
      button.textContent = '✓ Copied!';
    } catch (e) {
      const errorMsg = window.i18n ? window.i18n.t('share.copyFailed') : 'Failed to copy link. Please copy it manually.';
      alert(errorMsg);
    }
  }
}

/**
 * Copy embed code to clipboard
 * @param tournamentId - The tournament ID
 * @param event - The click event
 */
export async function copyEmbedCode(tournamentId: string, event: Event): Promise<void> {
  const textarea = document.getElementById(`embed-code-${tournamentId}`) as HTMLTextAreaElement | null;
  const button = event.target as HTMLButtonElement;

  if (!textarea) return;

  try {
    await navigator.clipboard.writeText(textarea.value);
    const originalText = button.textContent;
    button.textContent = '✓ Copied!';
    button.style.backgroundColor = '#28a745';

    setTimeout(() => {
      button.textContent = originalText;
      button.style.backgroundColor = '';
    }, 2000);
  } catch (err) {
    // Fallback for older browsers
    textarea.select();
    textarea.setSelectionRange(0, 99999);
    try {
      document.execCommand('copy');
      button.textContent = '✓ Copied!';
    } catch (e) {
      const errorMsg = window.i18n ? window.i18n.t('share.copyFailed') : 'Failed to copy code. Please copy it manually.';
      alert(errorMsg);
    }
  }
}

/**
 * Open share with user modal
 * @param tournamentId - The tournament ID
 */
export async function openShareModal(tournamentId: string): Promise<void> {
  const modal = document.getElementById('share-modal');
  const tournamentIdInput = document.getElementById('share-tournament-id') as HTMLInputElement | null;
  const errorDiv = document.getElementById('share-error');
  const successDiv = document.getElementById('share-success');
  const usernameInput = document.getElementById('share-username-email') as HTMLInputElement | null;

  if (!modal || !tournamentIdInput || !errorDiv || !successDiv || !usernameInput) return;

  // Reset form
  tournamentIdInput.value = tournamentId;
  usernameInput.value = '';
  errorDiv.textContent = '';
  successDiv.textContent = '';

  // Load current shared users
  await loadSharedUsers(tournamentId);

  modal.style.display = 'block';
}

/**
 * Close share with user modal
 */
export function closeShareModal(): void {
  const modal = document.getElementById('share-modal');
  if (modal) {
    modal.style.display = 'none';
  }
}

/**
 * Handle share with user form submission
 * @param event - The form submit event
 */
export async function handleShareWithUser(event: Event): Promise<void> {
  event.preventDefault();

  const tournamentIdInput = document.getElementById('share-tournament-id') as HTMLInputElement | null;
  const usernameInput = document.getElementById('share-username-email') as HTMLInputElement | null;
  const errorDiv = document.getElementById('share-error');
  const successDiv = document.getElementById('share-success');

  if (!tournamentIdInput || !usernameInput || !errorDiv || !successDiv) return;

  const tournamentId = tournamentIdInput.value;
  const usernameOrEmail = usernameInput.value;

  errorDiv.textContent = '';
  successDiv.textContent = '';

  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/share-with-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ usernameOrEmail })
    });

    const data: ShareWithUserResponse = await response.json();

    if (response.ok) {
      usernameInput.value = '';

      // Reload shared users list
      await loadSharedUsers(tournamentId);

      // Reload single tournament to update the UI
      if (window.loadSingleTournament) {
        window.loadSingleTournament(tournamentId);
      }
    } else {
      errorDiv.textContent = (data as any).error || 'Failed to share tournament';
    }
  } catch (error) {
    console.error('Error sharing tournament:', error);
    errorDiv.textContent = 'An error occurred while sharing the tournament';
  }
}

/**
 * Load and display shared users list
 * @param tournamentId - The tournament ID
 */
export async function loadSharedUsers(tournamentId: string): Promise<void> {
  const container = document.getElementById('shared-users-container');
  if (!container) return;

  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/shared-users`, {
      credentials: 'include'
    });

    const data: SharedUsersResponse = await response.json();

    if (response.ok && data.sharedUsers && data.sharedUsers.length > 0) {
      container.innerHTML = `
        <ul style="list-style: none; padding: 0;">
          ${data.sharedUsers.map(user => `
            <li style="display: flex; justify-content: space-between; align-items: center; padding: 10px; border-bottom: 1px solid #eee;">
              <div>
                <strong>${user.username}</strong>
                <span style="color: #666; margin-left: 10px;">${user.email}</span>
              </div>
              <button class="btn-icon" onclick="revokeShare('${tournamentId}', '${user.userId}', '${user.username}')" title="Revoke access">🗑</button>
            </li>
          `).join('')}
        </ul>
      `;
    } else {
      container.innerHTML = '<p style="color: #999; font-style: italic;">Not shared with any users yet.</p>';
    }
  } catch (error) {
    console.error('Error loading shared users:', error);
    container.innerHTML = '<p style="color: #ff4444;">Failed to load shared users</p>';
  }
}

/**
 * Revoke share access for a user
 * @param tournamentId - The tournament ID
 * @param userId - The user ID to revoke
 * @param username - The username for confirmation
 */
export async function revokeShare(tournamentId: string, userId: string, username: string): Promise<void> {
  try {
    const response = await fetch(`${API_BASE_URL}/${tournamentId}/share-with-user/${userId}`, {
      method: 'DELETE',
      credentials: 'include'
    });

    const data = await response.json();

    if (response.ok) {
      // Reload shared users list
      await loadSharedUsers(tournamentId);

      // Reload single tournament to update the UI
      if (window.loadSingleTournament) {
        window.loadSingleTournament(tournamentId);
      }
    } else {
      const errorMsg = window.i18n ? window.i18n.t('share.errorRevoke') : 'Failed to revoke access';
      alert(`Error: ${data.error || errorMsg}`);
    }
  } catch (error) {
    console.error('Error revoking share:', error);
    const errorMsg = window.i18n ? window.i18n.t('share.errorOccurredRevoke') : 'An error occurred while revoking access';
    alert(errorMsg);
  }
}

// Export to window for onclick handlers

window.toggleShareLink = toggleShareLink;
window.generateShareLink = generateShareLink;
window.copyShareLink = copyShareLink;
window.copyEmbedCode = copyEmbedCode;
window.openShareModal = openShareModal;
window.closeShareModal = closeShareModal;
window.handleShareWithUser = handleShareWithUser;
window.revokeShare = revokeShare;
